import axios from 'axios'

import { ProjectJobType } from './jobs'

const BASE_URL = process.env.REACT_APP_NEXTERA_API

export const listProjectTrackers = (
  projectId: string,
  floorCode?: string | null,
): Promise<{ data: ProjectTracker[] }> => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/trackers`, {
      params: {
        category: 'pt',
        floor: floorCode || undefined,
      },
    })
    .then(r => r.data)
}

export const listProjectSafetyTrackers = (
  projectId: string,
): Promise<{ data: ProjectTracker[] }> => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/trackers`, {
      params: {
        category: 'safety',
      },
    })
    .then(r => r.data)
}

export const fetchTracker = (
  projectId: string,
  tracker: string,
  observationDate?: Date,
  includeZones?: boolean,
) => {
  const endpoint = includeZones
    ? `${BASE_URL}/project/${projectId}/tracker/${tracker}/with-zones`
    : `${BASE_URL}/project/${projectId}/tracker/${tracker}`

  return axios
    .get(endpoint, {
      params: {
        observation_date: observationDate
          ? observationDate.toISOString()
          : undefined,
        include_zones: includeZones ? 'true' : undefined,
      },
    })
    .then(r => r.data)
}

type ProjectTrackerObservationParamNames = 'floor'
type ProjectTrackerObservationParams = Partial<
  Record<ProjectTrackerObservationParamNames, string>
>

export const fetchProjectTrackerObservations = async (
  projectId: string,
  trackerName: string,
  params?: ProjectTrackerObservationParams,
): Promise<ProjectTrackerObservation[]> => {
  const res = await axios.get(
    `${BASE_URL}/project/${projectId}/tracker/${trackerName}/observations`,
    {
      params: params,
    },
  )
  const response = await res.data
  return response.data
}

export interface TrackerType {
  name: string
  is_sequential: boolean
  display_order: number
}
export interface ProjectTracker {
  id: number
  type: TrackerType
  registered_on: string
  last_modified_on: string
  project: number
  types?: ProjectJobType[]
  floor_codes?: string[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper Record value type
export type ProjectTrackerDataZone = {
  zone_id: number
  zone_name: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper Record value type
} & Record<string, any>

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper Record value type
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper Record value type
export type ProjectTrackerDataFloor = {
  floor: string
  zones?: ProjectTrackerDataZone[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper Record value type
} & Record<string, any>

export interface ProjectTrackerData extends ProjectTracker {
  floors: ProjectTrackerDataFloor[]
}

export interface ProjectTrackerObservation {
  id: number
  registered_on: string
  last_modified_on: string
  job_type_id: number
  observed_quantity: number
  project_job_type_id: number
  signal: number
  is_adjusted: boolean
  approved: boolean
  reviewed: boolean
  is_revised: boolean
  job: number
  image: number
  revised_by_user: number | null
  item: number
}

export interface CloneFloorMappingOverride {
  source_viewpoint_id: number
  target_viewpoint_id: number
}

export interface CloneFloorRequest {
  source_floor_code: string
  target_floor_code: string
  mapping_overrides?: CloneFloorMappingOverride[]
  selected_source_viewpoint_ids?: number[]
  all_trackers?: boolean
  overwrite_existing?: boolean
  offset_x?: number
  offset_y?: number
}

export interface CloneFloorDryRunResponse {
  tracker: string
  source_floor_code: string
  target_floor_code: string
  mapping: CloneFloorMappingOverride[]
  counts: {
    items_to_create: number
    positions_to_create: number
    jobs_to_create: number
    items_to_replace: number
    items_skipped: number
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- backend returns heterogeneous objects
  skipped: any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- backend returns heterogeneous objects
  errors: any[]
  can_apply: boolean
  overwrite_existing?: boolean
  offset?: {
    x: number
    y: number
  }
}

export interface CloneFloorApplyResponse {
  tracker: string
  source_floor_code: string
  target_floor_code: string
  created: {
    item_ids: number[]
    items: number
    positions: number
    jobs: number
  }
  replaced_item_ids?: number[]
  overwrite_existing?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- backend returns heterogeneous objects
  skipped: any[]
  offset?: {
    x: number
    y: number
  }
}

export const cloneFloorDryRun = async (
  projectId: string,
  trackerName: string,
  payload: CloneFloorRequest,
): Promise<CloneFloorDryRunResponse> => {
  const response = await axios.post(
    `${BASE_URL}/project/${projectId}/tracker/${trackerName}/clone-floor/dry-run`,
    payload,
  )
  return response.data.data
}

export const cloneFloorApply = async (
  projectId: string,
  trackerName: string,
  payload: CloneFloorRequest,
): Promise<CloneFloorApplyResponse> => {
  const response = await axios.post(
    `${BASE_URL}/project/${projectId}/tracker/${trackerName}/clone-floor/apply`,
    payload,
  )
  return response.data.data
}
